/**
 * VellumTTSPlayer v3
 *
 * Changes:
 *  - Custom TTSSpeedControl replaces DiscordAudioPlayer SpeedControl
 *    • Horizontal slider 0.5–2.5 at 0.1 increments
 *    • Shows estimated reading duration
 *    • Named presets (Slow / Normal / Fast / Very Fast)
 *    • Quick-select buttons matching the screenshot
 *  - No component selectors (no @emotion/babel-plugin required)
 */

import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  LinearProgress,
  Popover,
  Slider,
  Tooltip,
  Typography,
} from '@mui/material';
import StopIcon from '@mui/icons-material/Stop';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { PlayPauseButton, VolumeControl } from '../DiscordAudioPlayer/components';
import { formatTime } from '../DiscordAudioPlayer/utils/formatTime';
import type { VellumTTSPlayerProps } from './VellumTTSPlayer.types';

// ── Animations ────────────────────────────────────────────────────────────────

const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

const generatingPulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50%       { opacity: 0.9; }
`;

// ── Styled atoms ──────────────────────────────────────────────────────────────

const PlayerRoot = styled.div<{ $visible: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1300;
  background: linear-gradient(135deg, #5865f2 0%, #4752c4 100%);
  box-shadow: 0 -4px 32px rgba(88, 101, 242, 0.35);
  transform: ${({ $visible }) => ($visible ? 'translateY(0)' : 'translateY(105%)')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition:
    transform 300ms cubic-bezier(0.4, 0, 0.2, 1),
    opacity 300ms cubic-bezier(0.4, 0, 0.2, 1);
  ${({ $visible }) =>
    $visible &&
    css`
      animation: ${slideUp} 280ms ease-out;
    `}
`;

const PlayerInner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px 14px;
  max-width: 900px;
  margin: 0 auto;
`;

const MetaColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 80px;
  flex-shrink: 0;
`;

const VoiceLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  font-family: var(--font-caption, sans-serif);
`;

const VoiceName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  font-family: var(--font-body, sans-serif);
`;

const ScrubColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
`;

const TimeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TimeText = styled.span`
  font-size: 11px;
  font-family: monospace;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  user-select: none;
`;

const GeneratingLabel = styled.span<{ $active: boolean }>`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.65);
  font-family: var(--font-caption, sans-serif);
  ${({ $active }) =>
    $active &&
    css`
      animation: ${generatingPulse} 1.4s ease-in-out infinite;
    `}
`;

const ScrubTrack = styled.div`
  position: relative;
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.18);
  cursor: pointer;
  user-select: none;
  touch-action: none;
  transition: height 120ms ease;

  &:hover {
    height: 6px;
  }
`;

const ScrubFill = styled.div<{ $width: number; $color: string }>`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: ${({ $width }) => $width}%;
  background: ${({ $color }) => $color};
  border-radius: 2px;
  transition: width 120ms linear;
  pointer-events: none;
`;

const ScrubThumb = styled.div<{ $position: number; $hovered: boolean }>`
  position: absolute;
  left: ${({ $position }) => $position}%;
  top: 50%;
  transform: translate(-50%, -50%) scale(${({ $hovered }) => ($hovered ? 1.2 : 1)});
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  transition: transform 120ms ease;
`;

interface ScrubbarProps {
  playProgress: number;
  bufferProgress: number;
  disabled?: boolean;
  onSeek: (percent: number) => void;
}

function Scrubbar({ playProgress, bufferProgress, disabled, onSeek }: ScrubbarProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [hovered, setHovered] = useState(false);

  const pct = useCallback((x: number) => {
    const trackRect = trackRef.current?.getBoundingClientRect();
    if (!trackRect) return 0;
    return Math.max(0, Math.min(100, ((x - trackRect.left) / trackRect.width) * 100));
  }, []);

  return (
    <ScrubTrack
      ref={trackRef}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onPointerDown={(e) => {
        if (disabled) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        dragging.current = true;
        onSeek(pct(e.clientX));
      }}
      onPointerMove={(e) => {
        if (dragging.current) onSeek(pct(e.clientX));
      }}
      onPointerUp={(e) => {
        e.currentTarget.releasePointerCapture(e.pointerId);
        dragging.current = false;
      }}
      onPointerCancel={() => {
        dragging.current = false;
      }}
      role="slider"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(playProgress)}
      aria-label="Playback position"
      tabIndex={disabled ? -1 : 0}
    >
      <ScrubFill $width={bufferProgress} $color="rgba(255,255,255,0.35)" />
      <ScrubFill $width={playProgress} $color="rgba(255,255,255,0.90)" />
      <ScrubThumb $position={playProgress} $hovered={hovered} />
    </ScrubTrack>
  );
}

// ── Speed control ─────────────────────────────────────────────────────────────

const SPEED_PRESETS = [0.8, 1.0, 1.2, 1.5, 2.0, 2.5] as const;

function speedLabel(rate: number): string {
  if (rate <= 0.7) return 'Very Slow';
  if (rate <= 0.9) return 'Slow';
  if (rate <= 1.1) return 'Normal';
  if (rate <= 1.4) return 'Fast';
  return 'Very Fast';
}

function formatDuration(sec: number): string {
  if (!sec || sec <= 0) return '';
  const elapsedMinutes = Math.round(sec / 60);
  if (elapsedMinutes < 1) return '< 1 min';
  return `~${elapsedMinutes} min`;
}

interface TTSSpeedControlProps {
  playbackRate: number;
  estimatedDuration: number;
  onSpeedChange: (rate: number) => void;
}

function TTSSpeedControl({ playbackRate, estimatedDuration, onSpeedChange }: TTSSpeedControlProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const rounded = Math.round(playbackRate * 10) / 10;

  return (
    <>
      <Tooltip title="Playback speed" placement="top">
        <Box
          component="button"
          onClick={(e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
          sx={{
            background: 'rgba(255,255,255,0.18)',
            border: 'none',
            borderRadius: '6px',
            color: '#fff',
            cursor: 'pointer',
            fontFamily: 'var(--font-body, sans-serif)',
            fontSize: '0.78rem',
            fontWeight: 700,
            letterSpacing: '0.02em',
            padding: '4px 9px',
            transition: 'background 140ms',
            '&:hover': { background: 'rgba(255,255,255,0.28)' },
          }}
          aria-label={`Speed: ${rounded}×`}
        >
          {rounded}×
        </Box>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              background: '#1a1a2e',
              borderRadius: '16px',
              boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
              padding: '20px 24px 24px',
              width: 300,
            },
          },
        }}
      >
        {/* Speed name + duration */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>
            {speedLabel(rounded)}
          </Typography>
          {estimatedDuration > 0 && (
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', mt: 0.5 }}>
              Duration: {formatDuration(estimatedDuration)}
            </Typography>
          )}
        </Box>

        {/* –/+ with big speed number */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}
        >
          <IconButton
            onClick={() => onSpeedChange(Math.max(0.5, Math.round((rounded - 0.1) * 10) / 10))}
            sx={{
              background: 'rgba(255,255,255,0.1)',
              color: '#fff',
              width: 40,
              height: 40,
              '&:hover': { background: 'rgba(255,255,255,0.18)' },
            }}
            aria-label="Decrease speed"
          >
            −
          </IconButton>
          <Typography
            sx={{
              color: '#fff',
              fontWeight: 800,
              fontSize: '2rem',
              minWidth: 72,
              textAlign: 'center',
            }}
          >
            {rounded}×
          </Typography>
          <IconButton
            onClick={() => onSpeedChange(Math.min(2.5, Math.round((rounded + 0.1) * 10) / 10))}
            sx={{
              background: 'rgba(255,255,255,0.1)',
              color: '#fff',
              width: 40,
              height: 40,
              '&:hover': { background: 'rgba(255,255,255,0.18)' },
            }}
            aria-label="Increase speed"
          >
            +
          </IconButton>
        </Box>

        {/* Slider */}
        <Slider
          value={rounded}
          min={0.5}
          max={2.5}
          step={0.1}
          onChange={(_, v) => onSpeedChange(v as number)}
          sx={{
            color: '#5865f2',
            mb: 2,
            '& .MuiSlider-thumb': { width: 18, height: 18 },
            '& .MuiSlider-rail': { opacity: 0.3 },
          }}
          aria-label="Playback speed"
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v}×`}
        />

        {/* Quick-select presets */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
          {SPEED_PRESETS.map((s) => (
            <Box
              key={s}
              component="button"
              onClick={() => {
                onSpeedChange(s);
                setAnchor(null);
              }}
              sx={{
                background: rounded === s ? '#5865f2' : 'rgba(255,255,255,0.08)',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '0.82rem',
                fontWeight: rounded === s ? 700 : 400,
                padding: '8px',
                transition: 'background 120ms',
                '&:hover': { background: rounded === s ? '#5865f2' : 'rgba(255,255,255,0.15)' },
              }}
            >
              {s}×
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
}

// ── Stop button ───────────────────────────────────────────────────────────────

const StopBtn = styled(IconButton)`
  && {
    color: rgba(255, 255, 255, 0.85);
    padding: 5px;
    &:hover {
      color: #fff;
      background: rgba(255, 255, 255, 0.12);
    }
  }
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

// ── Main component ────────────────────────────────────────────────────────────

export const VellumTTSPlayer: React.FC<VellumTTSPlayerProps> = ({
  visible,
  isPlaying,
  isPaused,
  isGenerating,
  playProgress,
  bufferProgress,
  currentPlayedSeconds,
  estimatedDuration,
  volume,
  isMuted,
  playbackRate,
  voiceName,
  voiceFlag,
  error,
  onPlayPause,
  onStop,
  onSeek,
  onVolumeChange,
  onToggleMute,
  onPlaybackRateChange,
}) => {
  const remaining = Math.max(0, estimatedDuration - currentPlayedSeconds);
  const isLoading = isGenerating && !isPlaying && !isPaused;

  return (
    <PlayerRoot $visible={visible} role="region" aria-label="Text-to-speech player">
      <PlayerInner>
        <MetaColumn>
          <VoiceLabel>Now reading</VoiceLabel>
          <VoiceName>
            {voiceFlag}
            {voiceFlag ? ' ' : null}
            {voiceName}
          </VoiceName>
        </MetaColumn>

        <PlayPauseButton
          isPlaying={isPlaying && !isPaused}
          isLoading={isLoading}
          size={38}
          iconSize={20}
          sizeVariant="medium"
          onClick={onPlayPause}
        />

        <ScrubColumn>
          <Scrubbar
            playProgress={playProgress}
            bufferProgress={bufferProgress}
            disabled={isLoading}
            onSeek={onSeek}
          />
          <TimeRow>
            <TimeText>{formatTime(currentPlayedSeconds)}</TimeText>
            {(isGenerating || error) && (
              <GeneratingLabel $active={isGenerating && !error}>
                {error ? (
                  <>
                    <WarningAmberIcon sx={{ fontSize: 12, verticalAlign: '-2px', mr: '4px' }} />
                    {error}
                  </>
                ) : (
                  'generating…'
                )}
              </GeneratingLabel>
            )}
            <TimeText>{estimatedDuration > 0 ? `-${formatTime(remaining)}` : '--:--'}</TimeText>
          </TimeRow>
        </ScrubColumn>

        <ControlsRow>
          <TTSSpeedControl
            playbackRate={playbackRate}
            estimatedDuration={estimatedDuration}
            onSpeedChange={onPlaybackRateChange}
          />
          <VolumeControl
            volume={volume}
            isMuted={isMuted}
            onVolumeChange={onVolumeChange}
            onToggleMute={onToggleMute}
          />
          <Tooltip title="Stop" placement="top">
            <StopBtn size="small" onClick={onStop} aria-label="Stop reading">
              <StopIcon fontSize="small" />
            </StopBtn>
          </Tooltip>
        </ControlsRow>
      </PlayerInner>

      {isGenerating && (
        <LinearProgress
          variant="determinate"
          value={bufferProgress}
          sx={{
            height: 2,
            backgroundColor: 'rgba(255,255,255,0.12)',
            '& .MuiLinearProgress-bar': { backgroundColor: 'rgba(255,255,255,0.55)' },
          }}
        />
      )}
    </PlayerRoot>
  );
};

export default VellumTTSPlayer;
